import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { CoreReportSourceResponse, CoreReportTypeResponse, ReportTreeNodeResponse, ReportCategoryResponse } from '../../../../core/components/reports/models/responses';
import { ReportSources } from '../../../../core/components/reports/models/enums';
import { ToastrService } from 'ngx-toastr';
import { ConsoleLogger } from '../../../../core/utilities/console-logger';
import { ReportsService } from '../../../../core/components/reports/reports.service';
import { first } from 'rxjs/operators';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ReportCategoryNameModalComponent } from './report-category-name-modal/report-category-name-modal.component';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmDialogService } from '../../../../core/components/confirm-dialog/confirm-dialog.service';
import { DivisionResponse, DivisionService } from '../../../../core/services/division.service';

@Component({
  selector: 'app-report-type-editor',
  templateUrl: './report-type-editor.component.html',
  styleUrls: ['./report-type-editor.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ReportTypeEditorComponent implements OnInit {

  @ViewChild(ReportCategoryNameModalComponent) reportCategoryNameModalComponent: ReportCategoryNameModalComponent;


  public selectedNode: ReportTreeNodeResponse | null = null;
  public selectedReportType: CoreReportTypeResponse | null = null;
  public reportTypeSources: CoreReportSourceResponse[] | null = null;
  public divisionList: DivisionResponse[] | null = null;

  public reportTypeForm: FormGroup;

  public reportSources = ReportSources;
  
  
  public expandFavorites = false;
  public reportTree: ReportTreeNodeResponse[] = [];
  public categoryList: ReportTreeNodeResponse[] = [];

  public fetchingReportType = false;
  public savingFavorites = false;
  public isLeafNode = false;
  public isSaving = false;
  public hideArchivedCategories = true;

  constructor(
    private confirmDialogService: ConfirmDialogService,
    private formBuilder: FormBuilder,
    private divisionService: DivisionService,
    private reportsService: ReportsService,
    private toastrService: ToastrService,
  ) { }

  ngOnInit() {
    this.divisionService.getActiveDivisions()
      .pipe(first())
      .subscribe({
        next: (divisions) => {
          ConsoleLogger.logInfo(`divisionService.getActiveDivisions()`, divisions);
          this.divisionList = divisions;
        },
        error: (error) => {
          ConsoleLogger.logInfo('Failed to load divisions', error);
          this.divisionList = [];
          this.toastrService.error('Failed to load divisions', 'Error');
        }
      });
    this.reportsService.getReportSources()
    .pipe(first())
    .subscribe((sources) => {
      ConsoleLogger.logInfo(`reportsService.getReportSources()`, sources);
      this.reportTypeSources = sources;
    }, () => {
      this.toastrService.error('Failed to load report sources', 'Error');
    });
    this.reportTypeForm = this.formBuilder.group({
      id: this.formBuilder.control(null),
      divisionId: this.formBuilder.control(null, [Validators.required]),
      isSaved: this.formBuilder.control(false),
      reportName: this.formBuilder.control('', [Validators.required]),
      tooltipDescription: this.formBuilder.control('', [Validators.required, Validators.maxLength(250)]),
      reportPath: this.formBuilder.control('', [Validators.required]),
      procedureName: this.formBuilder.control(''),
      internalNotes: this.formBuilder.control(''),
      externalNotes: this.formBuilder.control('', [Validators.required, Validators.maxLength(500)]),
      reportSourceId: this.formBuilder.control(null, [Validators.required]),
      reportCategoryId: this.formBuilder.control(null, [Validators.required]),
      isSystem: this.formBuilder.control(false),
      startDate: this.formBuilder.control( 
        new Date().toISOString().substring(0, 10),
        [Validators.required]
      ),
      endDate: this.formBuilder.control(null),
    });
    this.loadReportTree();

  }

  private loadReportTree(): void {
    this.reportsService.getReportTree(null, true)
      .pipe(first())
      .subscribe((tree: ReportTreeNodeResponse[]) => {
        ConsoleLogger.logInfo(`>>>>reportsService.getReportTree(${null})`, tree);
        if (tree && tree.length > 0) {
          this.reportTree = tree;

          // Build a flat list of all categories (nodes without sourceName) from the entire tree
          const allCategories: ReportTreeNodeResponse[] = [];
          this.collectCategories(tree, allCategories);
          this.categoryList = allCategories;

          this.sortTree(this.reportTree);
        }
      }, () => {
        this.toastrService.error('Failed to load report tree', 'Error');
      });

    // Keep divisionId in sync with the selected report category (parent)
    const reportCategoryControl = this.reportTypeForm.get('reportCategoryId');
    if (reportCategoryControl) {
      reportCategoryControl.valueChanges.subscribe((categoryId: number | null) => {
        if (categoryId == null) {
          return;
        }

        const categoryNode = this.findNodeById(this.reportTree, categoryId);
        if (!categoryNode) {
          return;
        }

        const divisionId = (categoryNode as any).divisionId != null
          ? (categoryNode as any).divisionId as number
          : null;

        this.reportTypeForm.patchValue(
          { divisionId: divisionId !== null ? divisionId : null },
          { emitEvent: false }
        );
      });
    }
  }

  private toDateInputValue(date: any): string | null {
    if (!date) {
      return null;
    }

    const d = new Date(date);
    if (isNaN(d.getTime())) {
      return null;
    }

    const year = d.getFullYear();
    const month = ('0' + (d.getMonth() + 1)).slice(-2);
    const day = ('0' + d.getDate()).slice(-2);

    return `${year}-${month}-${day}`;
  }

  private findNodeById(nodes: ReportTreeNodeResponse[], id: number): ReportTreeNodeResponse | null {
    for (const node of nodes) {
      if (node.id === id) {
        return node;
      }
      if (node.children && node.children.length > 0) {
        const found = this.findNodeById(node.children, id);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  private findParentNode(nodes: ReportTreeNodeResponse[], childId: number): ReportTreeNodeResponse | null {
    if (!nodes || nodes.length === 0) {
      return null;
    }

    for (const node of nodes) {
      if (node.children && node.children.some(c => c.id === childId)) {
        return node;
      }

      if (node.children && node.children.length > 0) {
        const foundParent = this.findParentNode(node.children, childId);
        if (foundParent) {
          return foundParent;
        }
      }
    }

    return null;
  }

  private sortTree(nodes: ReportTreeNodeResponse[]): void {
    if (!nodes || nodes.length === 0) {
      return;
    }

    nodes.sort((a, b) => {
      const aIsFolder = !a.sourceName;
      const bIsFolder = !b.sourceName;

      if (aIsFolder && !bIsFolder) {
        return -1;
      }
      if (!aIsFolder && bIsFolder) {
        return 1;
      }

      const aName = a.name || '';
      const bName = b.name || '';
      return aName.localeCompare(bName);
    });

    for (const node of nodes) {
      if (node.children && node.children.length > 0) {
        this.sortTree(node.children);
      }
    }
  }

  private collectCategories(nodes: ReportTreeNodeResponse[], result: ReportTreeNodeResponse[], pathName: string = ''): ReportTreeNodeResponse[] {
    if (!nodes || nodes.length === 0) {
      return result;
    }
    for (const node of nodes) {

      if (node.isActive && !node.sourceName) {
        // Create a shallow copy for the category list so we don't mutate the tree node's name
        const displayNode: ReportTreeNodeResponse = {
          ...node,
          name: pathName + node.name
        };
        result.push(displayNode);
      }

      if (node.isActive && node.children && node.children.length > 0) {
        const nextPath = pathName + node.name + ' > ';
        this.collectCategories(node.children, result, nextPath);
      }
    }
  }

  private updateCategoryDivisionInTree(categoryId: number, divisionId: number): void {
    if (!this.reportTree || this.reportTree.length === 0) {
      return;
    }

    const startNode = this.findNodeById(this.reportTree, categoryId);
    if (!startNode) {
      return;
    }

    const queue: ReportTreeNodeResponse[] = [startNode];

    while (queue.length > 0) {
      const node = queue.shift();
      if (!node) {
        continue;
      }

      // Only apply division updates to category nodes (no sourceId)
      if (!node.sourceId) {
        (node as any).divisionId = divisionId;
      }

      if (node.children && node.children.length > 0) {
        for (const child of node.children) {
          // Only traverse into category branches
          if (!child.sourceId) {
            queue.push(child);
          }
        }
      }
    }
  }

  private setDivisionFromBranch(node: ReportTreeNodeResponse | null): void {
    if (!node || !this.reportTypeForm) {
      return;
    }

    // Determine the category node that owns this report type / branch
    let categoryNode: ReportTreeNodeResponse | null = null;

    if (node.sourceId != null) {
      // Leaf report type node: use its parent category
      const parentId = node.parentCategoryId;
      categoryNode = parentId != null ? this.findNodeById(this.reportTree, parentId) : null;
    } else {
      // Category node itself
      categoryNode = node;
    }

    const divisionId = categoryNode && (categoryNode as any).divisionId != null
      ? (categoryNode as any).divisionId as number
      : null;

    this.reportTypeForm.patchValue({
      divisionId: divisionId !== null ? divisionId : null,
    });
  }

  onSubmit() {
    this.isSaving = true;
    if (!this.reportTypeForm.valid) {
      Object.values(this.reportTypeForm.controls).forEach(control => control.markAsTouched());
      this.isSaving = false;
      return;
    }
    const formValue = this.reportTypeForm.value as CoreReportTypeResponse;
    formValue.endDate = formValue.endDate ? new Date(formValue.endDate) : null;
    formValue.startDate = formValue.startDate ? new Date(formValue.startDate) : null;
    const isNew = !formValue.id || formValue.id === 0;

    this.reportsService.createReportType(formValue)
    .pipe(first())
    .subscribe((created) => {
      ConsoleLogger.logInfo('reportsService.createReportType', created);
      this.toastrService.success(
        isNew ? 'Report type created successfully' : 'Report type updated successfully',
        'Success'
      );
      this.selectedReportType = created;
      this.reportTypeForm.patchValue(created);
      this.reportTypeForm.patchValue({
        ...this.selectedReportType,
        startDate: this.toDateInputValue(this.selectedReportType.startDate as any),
        endDate: this.toDateInputValue(this.selectedReportType.endDate as any)
      });

      if (isNew && created && created.id) {
        const matchedSource = this.reportTypeSources && this.reportTypeSources.find(s => s.id === created.reportSourceId);
        const newNode: ReportTreeNodeResponse = {
          id: created.id,
          name: created.name || created.reportName,
          parentCategoryId: created.reportCategoryId,
          sourceId: created.reportSourceId,
          sourceName: matchedSource ? matchedSource.sourceName : undefined,
          isFavorite: false,
          roleIds: [],
          children: []
        };

        const parentNode: ReportTreeNodeResponse | null = null;

        const newParent = created.reportCategoryId != null
          ? this.findNodeById(this.reportTree, created.reportCategoryId)
          : null;

        if (newParent) {
          newParent.children.push(newNode);
        }
      } else if (!isNew && created && created.id) {
        // Existing report type: update the node name and, if the category changed, move the node in the local tree
        const currentNode = this.findNodeById(this.reportTree, created.id);
        if (currentNode) {
          // Keep the same naming logic used when creating a new node
          currentNode.name = created.name || created.reportName;
          currentNode.parentCategoryId = created.reportCategoryId;

          const oldParent = this.findParentNode(this.reportTree, created.id);
          const newParent = created.reportCategoryId != null
            ? this.findNodeById(this.reportTree, created.reportCategoryId)
            : null;

          if (oldParent !== newParent) {
            if (oldParent && oldParent.children) {
              oldParent.children = oldParent.children.filter(c => c.id !== created.id);
            }

            if (newParent) {
              if (!newParent.children) {
                newParent.children = [];
              }
              newParent.children.push(currentNode);
            }
          }
        }
      }
      this.sortTree(this.reportTree);
      this.isSaving = false;
    }, () => {
      this.toastrService.error('Failed to create report type', 'Error');
      this.isSaving = false;
    });
  }

  public onNodeClick(node: ReportTreeNodeResponse) {
  this.isLeafNode = (!node.children || node.children.length === 0) && node.sourceId != null;    this.selectedNode = node;
    if (this.isLeafNode) {
      this.fetchingReportType = true;
      this.selectedReportType = null;
      if (node.sourceName) {
        this.reportsService.getReportType(node.id)
          .pipe(first())
          .subscribe((reportType) => {
            ConsoleLogger.logInfo(`reportsService.getReportTree(${node.id})`, reportType);
            this.selectedReportType = reportType;
            this.reportTypeForm.patchValue({
              ...this.selectedReportType,
              startDate: this.toDateInputValue(this.selectedReportType.startDate as any),
              endDate: this.toDateInputValue(this.selectedReportType.endDate as any)
            });
            this.reportTypeForm.patchValue({
              reportCategoryId: node.parentCategoryId != null ? node.parentCategoryId : null,
            });
            this.setDivisionFromBranch(node);
            this.fetchingReportType = false;
          });
      } else {
        this.fetchingReportType = false;
      }
    } else {
      this.selectedReportType = null;
      this.reportTypeForm.reset({
        id: null,
        divisionId: null,
        isSaved: false,
        reportName: '',
        tooltipDescription: '',
        reportPath: '',
        procedureName: '',
        internalNotes: '',
        externalNotes: '',
        reportSourceId: null,
        reportCategoryId: this.selectedNode.id,
        isSystem: false,
        startDate: new Date().toISOString().substring(0, 10),
        endDate: null
      });
      this.setDivisionFromBranch(this.selectedNode);
    }
  }

  public onOpenNewCategoryModal(node: ReportTreeNodeResponse) {
    this.reportCategoryNameModalComponent.openAdd(node);
  }
  public onOpenEditCategoryModal(node: ReportTreeNodeResponse) {
    const foundParent = this.findParentNode(this.reportTree, node.id);
    this.reportCategoryNameModalComponent.openEdit(node, foundParent);
  }

  public onDuplicateReportType(node: ReportTreeNodeResponse) {
    if (!node || !node.id || !node.sourceId) {
      return;
    }
    const message = `Are you sure you want to duplicate report type: "${node.name}"?` 
    this.confirmDialogService.showConfirm(
      message,
    () => {

      this.reportsService.getReportType(node.id)
      .pipe(first())
      .subscribe((original) => {
        if (!original) {
          this.toastrService.error('Failed to load report type for duplication', 'Error');
          return;
        }

        const baseName = (original.reportName || original.name || '').toString();
        const truncatedBase = baseName.length > 93 ? baseName.substring(0, 93) : baseName;
        const newName = `${truncatedBase} (Copy)`;

        const duplicate: CoreReportTypeResponse = {
          ...original,
          id: 0,
          name: newName,
          reportName: newName,
          startDate: original.startDate ? new Date(original.startDate) : undefined,
          endDate: original.endDate ? new Date(original.endDate) : undefined,
        };

        this.reportsService.createReportType(duplicate)
          .pipe(first())
          .subscribe((created) => {
            if (!created || !created.id) {
              this.toastrService.error('Failed to create duplicated report type', 'Error');
              return;
            }

            this.toastrService.success('Report type duplicated successfully', 'Success');

            const matchedSource = this.reportTypeSources && this.reportTypeSources.find(s => s.id === created.reportSourceId);
            const newNode: ReportTreeNodeResponse = {
              id: created.id,
              name: created.name || created.reportName,
              parentCategoryId: created.reportCategoryId,
              sourceId: created.reportSourceId,
              sourceName: matchedSource ? matchedSource.sourceName : undefined,
              isActive: true,
              isFavorite: false,
              roleIds: [],
              children: [],
            };

            const parentCategoryId = created.reportCategoryId != null ? created.reportCategoryId : node.parentCategoryId;
            const parentNode = parentCategoryId != null ? this.findNodeById(this.reportTree, parentCategoryId) : null;

            if (parentNode) {
              if (!parentNode.children) {
                parentNode.children = [];
              }
              parentNode.children.push(newNode);
            } else {
              this.reportTree.push(newNode);
            }

            this.sortTree(this.reportTree);
          }, () => {
            this.toastrService.error('Failed to create duplicated report type', 'Error');
          });
      }, () => {
        this.toastrService.error('Failed to load report type for duplication', 'Error');
      });
    }, () => {})

  }

  public onCategorySaved(category: ReportCategoryResponse) {
    if (!category) {
      return;
    }

    // Try to find existing node in the current tree
    const existingNode = this.findNodeById(this.reportTree, category.id);

    if (existingNode) {
      // Update basic properties
      existingNode.name = category.categoryName;
      existingNode.parentCategoryId = category.parentCategoryId != null ? category.parentCategoryId : null;

      // Determine current and new parents
      const currentParent = this.findParentNode(this.reportTree, category.id);
      const newParent = category.parentCategoryId != null
        ? this.findNodeById(this.reportTree, category.parentCategoryId)
        : null;

      // If parent changed, move node in the local tree
      if (currentParent !== newParent) {
        if (currentParent && currentParent.children) {
          currentParent.children = currentParent.children.filter(c => c.id !== category.id);
        } else {
          // Node might be at root level
          this.reportTree = this.reportTree.filter(c => c.id !== category.id);
        }

        if (newParent) {
          if (!newParent.children) {
            newParent.children = [];
          }
          newParent.children.push(existingNode);
        } else {
          // Becomes a root-level category
          this.reportTree.push(existingNode);
        }
      }
    } else {
      // New category: create a node and insert it under its parent (or root)
      const newNode: ReportTreeNodeResponse = {
        id: category.id,
        name: category.categoryName,
        parentCategoryId: category.parentCategoryId != null ? category.parentCategoryId : null,
        isActive: true,
        sourceId: undefined,
        sourceName: undefined,
        isFavorite: null,
        roleIds: [],
        children: []
      };

      if (category.parentCategoryId != null) {
        const parent = this.findNodeById(this.reportTree, category.parentCategoryId);
        if (parent) {
          if (!parent.children) {
            parent.children = [];
          }
          parent.children.push(newNode);
        } else {
          // Parent not found in current tree; add as root to avoid losing it
          this.reportTree.push(newNode);
        }
      } else {
        // Root-level category
        this.reportTree.push(newNode);
      }
    }

    // After any structural changes, ensure the divisionId for this category and
    // all its child branches in the local tree reflect the latest value.
    const updatedDivisionId = (category as any).divisionId as number | undefined;
    if (updatedDivisionId !== undefined && updatedDivisionId !== null) {
      this.updateCategoryDivisionInTree(category.id, updatedDivisionId);
    }

    // Rebuild the flat category list used for dropdowns, preserving branch expansion
    const allCategories: ReportTreeNodeResponse[] = [];
    this.collectCategories(this.reportTree, allCategories);
    this.categoryList = allCategories;

    this.sortTree(this.reportTree);
  }

  public onToggleCategoryActive(node: ReportTreeNodeResponse) {
    if (!node || !node.id) {
      return;
    }
    const message = node.isActive ?
      `Are you sure you want to archive this category?` :
      `Are you sure you want to unarchive this category?`;
    this.confirmDialogService.showConfirm(
      message,
    () => {
      this.reportsService.toggleReportCategoryIsActive(node.id)
      .pipe(first())
      .subscribe(
        (updated) => {
          const target = this.findNodeById(this.reportTree, updated.id);
          if (target) {
            target.isActive = updated.isActive;
          }
          const allCategories: ReportTreeNodeResponse[] = [];
          this.collectCategories(this.reportTree, allCategories);
          this.categoryList = allCategories;
        },
        (err) => {
          this.toastrService.error('Failed to update category active state', 'Error');
          ConsoleLogger.logInfo('Error toggling report category IsActive', err);
        }
      );
    }, () => {})
  }

  public onCancel(): void {
    if (this.selectedNode) {
      this.onNodeClick(this.selectedNode);
      this.reportTypeForm.markAsPristine();
      this.reportTypeForm.markAsUntouched();
    }
  }
  
}



// WEBPACK FOOTER //
// ./src/app/developer/components/developer-tools/report-type-editor/report-type-editor.component.ts